import {
  Alert,
  Badge,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
} from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { QuotaWindow } from "./api";
import { requestErrorText } from "./requestErrorText";
import { useQuotas } from "./useQuotas";

// UI-индикатор квот на странице /queue.
//
// Для дневного и недельного окна рисует Mantine Progress (value =
// percent). Цвет бара: серый ниже порога soft pause, жёлтый при
// достижении порога, красный на 100%. Подпись — scope + percent от
// порога + прогноз часов до паузы (или «—», если прогноза нет). При
// percent >= порога — Alert с предупреждением окна. При `pausedByQuota`
// — оранжевый Badge «приостановлено по квоте».
//
// Окна квот компонент забирает сам — `useQuotas` (polling 2с). А вот
// флага паузы в сводке квот нет: он живёт на очереди
// (`TaskQueueResponse.paused_by_quota`), см.
// `backend/modules/quota/schemas.py`. Поэтому флаг приходит пропом от
// родителя (`QueuePage`), которому `useQueue` нужен и так — второй
// подписки на очередь не заводим.

// Полный процент окна: на 100% бар становится красным (квота исчерпана).
const FULL_PERCENT = 100;

function barColor(percent: number, threshold: number): string {
  if (percent >= FULL_PERCENT) {
    return "red";
  }
  if (percent >= threshold) {
    return "yellow";
  }
  return "gray";
}

function forecastText(window: QuotaWindow, t: TFunction): string {
  if (window.hours_to_pause === null) {
    return t("quotas.noData");
  }
  // Округляем до одного знака: «~0.8 ч до паузы» читабельнее, чем сырое
  // 0.7999999. Math.round(x*10)/10 — без зависимости на toFixed-строку.
  const hours = Math.round(window.hours_to_pause * 10) / 10;
  return t("quotas.hoursToPause", { hours });
}

function QuotaBar({
  window,
  label,
}: {
  readonly window: QuotaWindow;
  readonly label: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="sm" c="dimmed">
          {t("quotas.percentLabel", {
            percent: window.percent,
            threshold: window.threshold_percent,
          })}{" "}
          · {forecastText(window, t)}
        </Text>
      </Group>
      <Progress
        value={window.percent}
        color={barColor(window.percent, window.threshold_percent)}
        aria-label={label}
      />
    </Stack>
  );
}

export function QuotaIndicator({
  pausedByQuota,
}: {
  // Флаг паузы очереди по квоте (`TaskQueueResponse.paused_by_quota`,
  // из `useQueue()` родителя) — сводка квот его не содержит.
  readonly pausedByQuota: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const quotas = useQuotas();

  if (quotas.isLoading) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          {t("quotas.loading")}
        </Text>
      </Group>
    );
  }

  if (quotas.isError) {
    return (
      <Alert color="red" title={t("quotas.loadError")}>
        {requestErrorText(quotas.error, t)}
      </Alert>
    );
  }

  const data = quotas.data;
  if (data === undefined) {
    return null;
  }

  const sessionOverThreshold =
    data.session.percent >= data.session.threshold_percent;
  const weeklyOverThreshold =
    data.weekly.percent >= data.weekly.threshold_percent;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={600}>{t("quotas.title")}</Text>
        {pausedByQuota ? (
          <Badge color="orange">{t("quotas.pausedBadge")}</Badge>
        ) : null}
      </Group>

      <QuotaBar window={data.session} label={t("quotas.session")} />
      <QuotaBar window={data.weekly} label={t("quotas.weekly")} />

      {Object.entries(data.per_model).map(([model, window]) => (
        <QuotaBar
          key={model}
          window={window}
          label={t("quotas.perModelLabel", { model })}
        />
      ))}

      {sessionOverThreshold ? (
        <Alert color={data.session.percent >= FULL_PERCENT ? "red" : "yellow"}>
          {t("quotas.sessionLimitAlert")}
        </Alert>
      ) : null}
      {weeklyOverThreshold ? (
        <Alert color={data.weekly.percent >= FULL_PERCENT ? "red" : "yellow"}>
          {t("quotas.weeklyLimitAlert")}
        </Alert>
      ) : null}
    </Stack>
  );
}
